import Vue from 'vue';
import AiAssistAPI from '../../api/aiAssist';

/**
 * AI Assist panel state (Inbox Reply Mode = Semi-Auto), keyed by conversation display id.
 *
 * Runs arrive three ways and all go through UPSERT_RUN:
 *   - `fetch` on conversation open restores `{ mode, trigger, idleSeconds, summary, replies }`
 *     from singabot (`trigger` 'wait' = automatic summary after `idleSeconds`; 'manual' = the
 *     panel shows Generate, which dispatches `regenerate`),
 *   - `regenerate` / `requestReply` return the new `pending` run (HTTP 202),
 *   - the `conversation.ai_assist` ActionCable event pushes every status change.
 *
 * A summary run with a different id starts a new suggestion set: it replaces the summary and
 * clears the replies (they belong to the previous summary). Replies are kept newest first.
 */

export const MUTATIONS = {
  SET_STATE: 'AI_ASSIST_SET_STATE',
  UPSERT_RUN: 'AI_ASSIST_UPSERT_RUN',
  SET_UI_FLAG: 'AI_ASSIST_SET_UI_FLAG',
  SET_INSERTED_SUGGESTION: 'AI_ASSIST_SET_INSERTED_SUGGESTION',
};

const defaultUIFlags = () => ({
  isFetching: false,
  isRegenerating: false,
  // action_index values with a reply request in flight
  requestingActions: [],
});

const emptyRecord = () => ({
  mode: null,
  trigger: null,
  idleSeconds: null,
  summary: null,
  replies: [],
  uiFlags: defaultUIFlags(),
});

const EMPTY_RECORD = Object.freeze({
  ...emptyRecord(),
  replies: Object.freeze([]),
  uiFlags: Object.freeze(defaultUIFlags()),
});

/**
 * Normalizes a singabot error (`{ detail: { code, message } }` through the proxy) into an Error
 * carrying `code` and `status`, so the panel can branch on `insufficient_credits`,
 * `already_running`, `not_semi_auto`, ... without digging into axios internals.
 */
export const toAiAssistError = error => {
  const detail = error?.response?.data?.detail;
  const message =
    (detail && typeof detail === 'object' ? detail.message : detail) ||
    error?.message ||
    'AI Assist request failed';
  const aiError = new Error(message);
  aiError.code = detail && typeof detail === 'object' ? detail.code : null;
  aiError.status = error?.response?.status ?? null;
  return aiError;
};

const sortNewestFirst = runs => [...runs].sort((a, b) => b.id - a.id);

export const state = {
  records: {},
  // `{ runId, text }` per conversation: the suggestion the agent put in the reply box.
  // Kept apart from `records` so fetch / SET_STATE never wipe it.
  insertedSuggestions: {},
};

export const getters = {
  getConversationState: _state => conversationId =>
    _state.records[conversationId] || EMPTY_RECORD,
  getMode: (_state, _getters) => conversationId =>
    _getters.getConversationState(conversationId).mode,
  getTrigger: (_state, _getters) => conversationId =>
    _getters.getConversationState(conversationId).trigger,
  getSummary: (_state, _getters) => conversationId =>
    _getters.getConversationState(conversationId).summary,
  getReplies: (_state, _getters) => conversationId =>
    _getters.getConversationState(conversationId).replies,
  getUIFlags: (_state, _getters) => conversationId =>
    _getters.getConversationState(conversationId).uiFlags,
  getInsertedSuggestion: _state => conversationId =>
    _state.insertedSuggestions[conversationId] || null,
};

function ensureRecord(_state, conversationId) {
  if (!_state.records[conversationId]) {
    Vue.set(_state.records, conversationId, emptyRecord());
  }
  return _state.records[conversationId];
}

export const mutations = {
  [MUTATIONS.SET_STATE](
    _state,
    { conversationId, mode, trigger, idleSeconds, summary, replies }
  ) {
    const record = ensureRecord(_state, conversationId);
    record.mode = mode ?? null;
    record.trigger = trigger ?? null;
    record.idleSeconds = idleSeconds ?? null;
    record.summary = summary || null;
    record.replies = sortNewestFirst(replies || []);
  },

  [MUTATIONS.UPSERT_RUN](_state, run) {
    if (!run || run.id === undefined || run.conversation_id === undefined) {
      return;
    }
    const record = ensureRecord(_state, run.conversation_id);

    if (run.kind === 'summary') {
      const current = record.summary;
      // ids are monotonic: a late push for an older summary never overrides a newer one.
      if (current && run.id < current.id) return;
      if (!current || current.id !== run.id) record.replies = [];
      record.summary = run;
      return;
    }

    if (run.kind === 'reply') {
      // A reply for a summary that is no longer current belongs to a discarded set.
      if (record.summary && run.parent_id !== record.summary.id) return;
      const others = record.replies.filter(reply => reply.id !== run.id);
      record.replies = sortNewestFirst([...others, run]);
    }
  },

  [MUTATIONS.SET_UI_FLAG](_state, { conversationId, flags }) {
    const record = ensureRecord(_state, conversationId);
    record.uiFlags = { ...record.uiFlags, ...flags };
  },

  [MUTATIONS.SET_INSERTED_SUGGESTION](_state, { conversationId, suggestion }) {
    if (suggestion) {
      Vue.set(_state.insertedSuggestions, conversationId, suggestion);
    } else {
      Vue.delete(_state.insertedSuggestions, conversationId);
    }
  },
};

export const actions = {
  async fetch({ commit }, { conversationId }) {
    commit(MUTATIONS.SET_UI_FLAG, {
      conversationId,
      flags: { isFetching: true },
    });
    try {
      const { data } = await AiAssistAPI.getState(conversationId);
      commit(MUTATIONS.SET_STATE, {
        conversationId,
        mode: data.mode,
        trigger: data.trigger,
        idleSeconds: data.idle_seconds,
        summary: data.summary,
        replies: data.replies,
      });
      return data;
    } catch (error) {
      throw toAiAssistError(error);
    } finally {
      commit(MUTATIONS.SET_UI_FLAG, {
        conversationId,
        flags: { isFetching: false },
      });
    }
  },

  async regenerate({ commit }, { conversationId }) {
    commit(MUTATIONS.SET_UI_FLAG, {
      conversationId,
      flags: { isRegenerating: true },
    });
    try {
      const { data: run } = await AiAssistAPI.regenerateSummary(conversationId);
      commit(MUTATIONS.UPSERT_RUN, run);
      return run;
    } catch (error) {
      throw toAiAssistError(error);
    } finally {
      commit(MUTATIONS.SET_UI_FLAG, {
        conversationId,
        flags: { isRegenerating: false },
      });
    }
  },

  async requestReply(
    { commit, getters: _getters },
    { conversationId, summaryRunId, actionIndex }
  ) {
    const requesting = () =>
      _getters.getUIFlags(conversationId).requestingActions;
    commit(MUTATIONS.SET_UI_FLAG, {
      conversationId,
      flags: { requestingActions: [...requesting(), actionIndex] },
    });
    try {
      const { data: run } = await AiAssistAPI.requestReply(conversationId, {
        summaryRunId,
        actionIndex,
      });
      commit(MUTATIONS.UPSERT_RUN, run);
      return run;
    } catch (error) {
      throw toAiAssistError(error);
    } finally {
      commit(MUTATIONS.SET_UI_FLAG, {
        conversationId,
        flags: {
          requestingActions: requesting().filter(i => i !== actionIndex),
        },
      });
    }
  },

  /** ActionCable `conversation.ai_assist` and POST responses both land here. */
  upsertRun({ commit }, run) {
    commit(MUTATIONS.UPSERT_RUN, run);
  },

  setInsertedSuggestion({ commit }, { conversationId, suggestion }) {
    commit(MUTATIONS.SET_INSERTED_SUGGESTION, { conversationId, suggestion });
  },

  clearInsertedSuggestion({ commit }, { conversationId }) {
    commit(MUTATIONS.SET_INSERTED_SUGGESTION, {
      conversationId,
      suggestion: null,
    });
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
